import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { storage } from '../utils/storage';
import {
  getAutoTourFeature,
  getTourForFeature,
  featureHasTour,
} from '../help/registry';

const TourContext = createContext();

export const useTour = () => {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error('useTour must be used within a TourProvider');
  }
  return context;
};

// NER-1276: tour steps now live in src/help/registry.js (HELP_REGISTRY[*].tour),
// keyed by feature. The legacy hardcoded TOUR_STEPS object is gone — adding a
// feature tour no longer means editing this context.

const COMPLETED_KEY = 'tourCompleted'; // JSON object: { [feature]: true }
const SKIP_KEY = 'tourSkipped'; // master switch: user opted out of auto-tours

/**
 * Reads the per-feature completed map from storage. Migrates the legacy global
 * value (the string 'true', which used to block every sub-tour) to an empty
 * map so that per-feature onboarding can run again.
 */
function readCompleted() {
  const raw = storage.get(COMPLETED_KEY);
  if (!raw || raw === 'true' || raw === 'false') return {};
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

export const TourProvider = ({ children }) => {
  const location = useLocation();
  const [isActive, setIsActive] = useState(false);
  const [currentFeature, setCurrentFeature] = useState('dashboard');
  const [currentStep, setCurrentStep] = useState(0);
  const [completed, setCompleted] = useState(readCompleted);

  const steps = getTourForFeature(currentFeature);
  const currentStepData = steps[currentStep];
  const totalSteps = steps.length;

  const persistCompleted = useCallback((next) => {
    setCompleted(next);
    storage.set(COMPLETED_KEY, JSON.stringify(next));
  }, []);

  const markFeatureCompleted = useCallback(
    (feature) => {
      persistCompleted({ ...readCompleted(), [feature]: true });
    },
    [persistCompleted]
  );

  const startTour = useCallback((feature = 'dashboard') => {
    if (!featureHasTour(feature)) {
      // Backward-compat: legacy callers passed page names that no longer map
      // to a registered tour. Warn so we can migrate them, but don't throw.
      // eslint-disable-next-line no-console
      console.warn(
        `[TourContext] startTour('${feature}'): no tour registered for this feature. ` +
          'Add it to src/help/registry.js (see src/help/README.md).'
      );
      return;
    }
    setCurrentFeature(feature);
    setCurrentStep(0);
    setIsActive(true);
  }, []);

  const stopTour = useCallback(() => {
    setIsActive(false);
    setCurrentStep(0);
  }, []);

  const nextStep = useCallback(() => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep((prev) => prev + 1);
    } else {
      setIsActive(false);
      setCurrentStep(0);
      markFeatureCompleted(currentFeature);
    }
  }, [currentStep, totalSteps, currentFeature, markFeatureCompleted]);

  const prevStep = useCallback(() => {
    setCurrentStep((prev) => (prev > 0 ? prev - 1 : prev));
  }, []);

  const skipTour = useCallback(() => {
    setIsActive(false);
    setCurrentStep(0);
    // Skipping marks the current feature done (so it won't auto-trigger again)
    // and disables further auto-tours for this user.
    markFeatureCompleted(currentFeature);
    storage.set(SKIP_KEY, 'true');
  }, [currentFeature, markFeatureCompleted]);

  const goToStep = useCallback(
    (stepIndex) => {
      if (stepIndex >= 0 && stepIndex < totalSteps) {
        setCurrentStep(stepIndex);
      }
    },
    [totalSteps]
  );

  // Backward-compat alias kept for older callers.
  const setPage = useCallback((feature) => {
    if (featureHasTour(feature)) {
      setCurrentFeature(feature);
      setCurrentStep(0);
    }
  }, []);

  const isFeatureCompleted = useCallback((feature) => !!readCompleted()[feature], []);

  const resetTour = useCallback(() => {
    storage.remove(COMPLETED_KEY);
    storage.remove(SKIP_KEY);
    setCompleted({});
  }, []);

  // Capa 2: route-aware auto-trigger. When the path matches a feature tour the
  // user hasn't completed (and hasn't opted out), start it once.
  const lastAutoPath = useRef(null);
  useEffect(() => {
    if (isActive) return;
    if (storage.get(SKIP_KEY) === 'true') return;
    const feature = getAutoTourFeature(location.pathname);
    if (!feature) return;
    if (completed[feature]) return;
    if (lastAutoPath.current === location.pathname) return;
    lastAutoPath.current = location.pathname;
    // Let the destination page mount before measuring tour targets.
    const timer = setTimeout(() => {
      setCurrentFeature(feature);
      setCurrentStep(0);
      setIsActive(true);
    }, 1200);
    return () => clearTimeout(timer);
  }, [location.pathname, isActive, completed]);

  const value = {
    isActive,
    currentFeature,
    currentStep,
    currentStepData,
    totalSteps,
    steps,
    completed,
    startTour,
    stopTour,
    nextStep,
    prevStep,
    skipTour,
    goToStep,
    setPage,
    resetTour,
    isFeatureCompleted,
    // Legacy alias — some callers still read `currentPage`.
    currentPage: currentFeature,
  };

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
};

export default TourContext;
